import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    setPage,
    setPageSize,
    setPageTab,
    setSearchValue,
    setKeyword,
    setSorting,
    setEditedUser,
    openEditModal,
    closeMessageModal,
    clearFilters,
} from '../../store/usersSlice';
import {
    getHeaders,
    headerWidths,
    reportsHeaderWidths,
    messageReportsHeaderWidths,
    pageTabsConfig,
    deleteSelected,
} from './profilesTableConfig.js';
import { fetchUsers, mapUsersToRows } from './profilesTableService.js';
import { extractProfilesEntries, normalizeProfilesToUsers } from './profilesAdapter.js';
import ApiService from '../../components/api_services/apiService.jsx';
import Modal from '../../components/components/modal/modal.jsx';
import Table from '../../components/list2/table';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import PageTabs from '../../components/components/page_tabs/PageTabs.jsx';
import Pagination from '../../components/components/pagination/pagination.jsx';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import CustomButton from '../../components/components/custom_button/customButton.jsx';
import CustomInput from '../../components/components/custom_input/customInput.jsx';
import UserDetailPanel from '../../components/components/user_detail_panel/UserDetailPanel.jsx';
import UserEditPanel from '../../components/components/user_edit_panel/UserEditPanel.jsx';
import UserMessageModal from '../../components/components/user_message_modal/UserMessageModal.jsx';
import MessageReportDetailsModal from '../../components/components/message_report_modal/MessageReportDetailsModal.jsx';
import ProfileImagePreview from '../../components/components/profile_image_preview/ProfileImagePreview.jsx';
import { ReactComponent as SearchIcon } from '../../components/icons/search.svg';
import { ReactComponent as Trash } from '../../components/icons/trash.svg';

import './Profiles.css';

function Profiles() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const totalPages = useSelector((state) => state.users.maxPages);
    const totalCount = useSelector((state) => state.users.totalCount);
    const currentPage = useSelector((state) => state.users.currentPage);
    const pageSize = useSelector((state) => state.users.pageSize || 10);
    const sortBy = useSelector((state) => state.users.sortBy);
    const sortDirection = useSelector((state) => state.users.sortDirection);
    const checkedItems = useSelector((state) => state.users.checkedItems);
    const keyword = useSelector((state) => state.users.keyword);
    const searchValue = useSelector((state) => state.users.searchValue);
    const pageTab = useSelector((state) => state.users.pageTab);
    const filters = useSelector((state) => state.users.filters);
    const editModalOpen = useSelector((state) => state.users.editModalOpen);
    const editedUser = useSelector((state) => state.users.editedUser);
    const messageModalOpen = useSelector((state) => state.users.messageModalOpen);
    const messageUser = useSelector((state) => state.users.messageUser);
    const userEmail = useSelector((state) => state.tokens.userEmail);

    const [rawUsers, setRawUsers] = useState([]);
    const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
    const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
    const [reportPreviewUser, setReportPreviewUser] = useState(null);
    const [reportPreviewMeta, setReportPreviewMeta] = useState(null);
    const [messageReportPreviewOpen, setMessageReportPreviewOpen] = useState(false);
    const [messageReportPreview, setMessageReportPreview] = useState(null);
    const [messageReportPreviewTab, setMessageReportPreviewTab] = useState('report');
    const [resolvingMessageReport, setResolvingMessageReport] = useState(false);
    const [messageReportsGrouping, setMessageReportsGrouping] = useState('REPORT');
    const sortArrowColorActive = '#060606';
    const sortArrowColorInactive = '#06060650';

    const moveActionsToSecondColumn = useCallback((items = []) => {
        if (!Array.isArray(items) || items.length < 3) return items;
        const [checkboxColumn, ...rest] = items;
        const actionsColumn = rest[rest.length - 1];
        const dataColumns = rest.slice(0, -1);
        return [checkboxColumn, actionsColumn, ...dataColumns];
    }, []);

    const openReportPreview = useCallback(async (reportRow) => {
        const reportedUserId = reportRow?.reportedUserId || reportRow?.profileUuid;
        if (!reportedUserId) {
            return;
        }

        setReportPreviewOpen(true);
        setReportPreviewLoading(true);
        setReportPreviewMeta(reportRow);

        try {
            const profile = await ApiService.getProfileById(reportedUserId);
            setReportPreviewUser(
                profile?.data
                ?? profile?.profileResponseDto
                ?? profile?.profile
                ?? profile
                ?? null
            );
        } catch (error) {
            console.error('Nie udało się pobrać szczegółów profilu zgłoszonego użytkownika:', error);
            setReportPreviewUser(null);
        } finally {
            setReportPreviewLoading(false);
        }
    }, []);

    const closeReportPreview = useCallback(() => {
        setReportPreviewOpen(false);
        setReportPreviewUser(null);
        setReportPreviewMeta(null);
        setReportPreviewLoading(false);
    }, []);

    const openMessageReportPreview = useCallback((report) => {
        setMessageReportPreview(report);
        setMessageReportPreviewTab(
            report?.groupType === 'MESSAGE' ? 'message' : report?.groupType === 'CHAT' ? 'chat' : 'report'
        );
        setMessageReportPreviewOpen(true);
    }, []);

    const closeMessageReportPreview = useCallback(() => {
        setMessageReportPreviewOpen(false);
        setMessageReportPreview(null);
        setMessageReportPreviewTab('report');
    }, []);

    const resolveMessageReportGroup = useCallback(async (report) => {
        if (!report?.reportGroupId && !report?.reportIds?.length) return;
        setResolvingMessageReport(true);
        try {
            if (report.reportGroupId) {
                await ApiService.resolveMessageReportGroup(report.reportGroupId);
            } else {
                await ApiService.resolveMessageReports(report.reportIds);
            }
            closeMessageReportPreview();
            setRawUsers((users) => users.filter((item) => item.reportGroupId !== report.reportGroupId));
        } catch (error) {
            console.error('Nie udało się rozwiązać grupy zgłoszeń wiadomości:', error);
        } finally {
            setResolvingMessageReport(false);
        }
    }, [closeMessageReportPreview]);

    const baseRows = useMemo(
        () => mapUsersToRows(rawUsers, checkedItems, dispatch, pageTab, {
            onReportDetails: openReportPreview,
            onMessageReportDetails: openMessageReportPreview,
        }),
        [rawUsers, checkedItems, dispatch, pageTab, openReportPreview, openMessageReportPreview]
    );
    const rows = useMemo(() => baseRows.map((row) => moveActionsToSecondColumn(row)), [baseRows, moveActionsToSecondColumn]);

    const handlePageTabChange = useCallback((tabId) => {
        if (location.pathname === '/reports') {
            navigate('/users', { replace: true });
        }
        dispatch(setPageTab(tabId));
        dispatch(setPage(1));
    }, [dispatch, location.pathname, navigate]);

    useEffect(() => {
        const controller = new AbortController();
        fetchUsers(
            currentPage,
            sortBy,
            sortDirection,
            keyword,
            pageTab,
            filters,
            dispatch,
            setRawUsers,
            { signal: controller.signal },
            messageReportsGrouping,
            pageSize
        );

        return () => {
            controller.abort();
        };
    }, [currentPage, pageSize, sortBy, sortDirection, keyword, pageTab, filters, dispatch, messageReportsGrouping]);

    useEffect(() => {
        if (sortBy && sortDirection) {
            return;
        }

        const defaultSortBy = pageTab === 'reports'
            ? 'reportDate'
            : pageTab === 'messageReports' ? 'lastReportedAt' : 'registrationDate';
        dispatch(setSorting({ sortBy: defaultSortBy, sortDirection: 'DESC' }));
    }, [pageTab, sortBy, sortDirection, dispatch]);

    useEffect(() => {
        if (location.pathname === '/reports' && pageTab !== 'reports') {
            dispatch(setPageTab('reports'));
        }
        if (location.state?.pageTab === 'messageReports') {
            if (pageTab !== 'messageReports') {
                dispatch(setPageTab('messageReports'));
            }
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.pathname, location.state, pageTab, navigate, dispatch]);

    useEffect(() => {
        const preview = location.state?.messageReportPreview;
        if (!preview) return;

        setMessageReportPreview(preview);
        setMessageReportPreviewTab(
            preview?.groupType === 'MESSAGE' ? 'message' : preview?.groupType === 'CHAT' ? 'chat' : 'report'
        );
        setMessageReportPreviewOpen(true);
        navigate(location.pathname, { replace: true, state: null });
    }, [location.key, location.pathname, location.state, navigate]);

    useEffect(() => {
        const shouldOpenOwnProfileEdit = Boolean(location.state?.openOwnProfileEdit);
        if (!shouldOpenOwnProfileEdit) {
            return undefined;
        }

        let isActive = true;

        const consumeNavigationState = () => {
            navigate(location.pathname, { replace: true, state: null });
        };

        const normalizedEmail = String(userEmail || '').trim().toLowerCase();

        const openOwnProfileEditor = async () => {
            try {
                dispatch(setPageTab('admins'));
                dispatch(clearFilters());
                dispatch(setPage(1));

                if (!normalizedEmail) {
                    return;
                }

                dispatch(setSearchValue(normalizedEmail));
                dispatch(setKeyword(normalizedEmail));

                const response = await ApiService.getSystemAdminProfiles({
                    pageNumber: 1,
                    pageSize: 50,
                    roleScope: 'ALL_USERS',
                    searchText: normalizedEmail,
                });

                if (!isActive) {
                    return;
                }

                const entries = extractProfilesEntries(response);
                const normalizedUsers = normalizeProfilesToUsers(entries);
                const currentUser = normalizedUsers.find((user) => String(user.email || '').trim().toLowerCase() === normalizedEmail);

                if (!currentUser) {
                    return;
                }

                setRawUsers((previousUsers) => (
                    previousUsers.some((user) => user.id === currentUser.id)
                        ? previousUsers
                        : [currentUser, ...previousUsers]
                ));

                dispatch(setEditedUser(currentUser));
                dispatch(openEditModal());
            } catch (error) {
                console.error('Nie udało się otworzyć edycji zalogowanego użytkownika:', error);
            } finally {
                if (isActive) {
                    consumeNavigationState();
                }
            }
        };

        openOwnProfileEditor();

        return () => {
            isActive = false;
        };
    }, [location.key, location.pathname, location.state, navigate, userEmail, dispatch]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        dispatch(setSearchValue(value));
        if (value.length >= 3) {
            dispatch(setKeyword(value));
        } else {
            dispatch(setKeyword(''));
        }
    };

    const handleDeleteSelected = () => {
        deleteSelected(checkedItems);
    };

    const baseHeaders = getHeaders(
        sortBy,
        sortDirection,
        dispatch,
        sortArrowColorActive,
        sortArrowColorInactive,
        rawUsers,
        checkedItems,
        pageTab
    );

    const headers = useMemo(() => moveActionsToSecondColumn(baseHeaders), [baseHeaders, moveActionsToSecondColumn]);
    const baseHeaderWidths = pageTab === 'reports'
        ? reportsHeaderWidths
        : pageTab === 'messageReports' ? messageReportsHeaderWidths : headerWidths;
    const currentHeaderWidths = useMemo(() => moveActionsToSecondColumn(baseHeaderWidths), [baseHeaderWidths, moveActionsToSecondColumn]);

    const topLeftSide = [
        {
            component: PageTabs,
            props: {
                tabs: pageTabsConfig.map((tab) => ({ ...tab, count: undefined })),
                onTabChange: handlePageTabChange,
                activeTab: pageTab,
            },
        },
    ];

    const topRightSide = [
        {
            component: CustomInput,
            props: {
                icon: SearchIcon,
                placeholder: 'Wyszukaj',
                hoverClass: 'hover-effect',
                borders: 'LRBT',
                bordersClassPrefix: 'border-black-',
                baseClass: 'custom-input',
                activeClass: '',
                value: searchValue,
                onChange: handleSearchChange,
            },
        },
    ];

    const bottomLeftSide = [
        {
            component: CustomButton,
            props: {
                icon: Trash,
                name: 'Usuń zaznaczone',
                baseClass: 'custom-button-white',
                bordersClassPrefix: 'border-black-',
                hoverClass: 'hover-effect',
                activeClass: '',
                borders: 'LRBT',
                onClick: handleDeleteSelected,
                toggleActive: false,
            },
        },
    ];

    const bottomRightSide = [
        {
            component: Pagination,
            props: {
                reduxSelector: (state) => state.users.currentPage,
                reduxAction: setPage,
                totalPages,
                maxVisibleButtons: 4,
                reduxPageSizeSelector: (state) => state.users.pageSize,
                reduxPageSizeAction: setPageSize,
                pageSizeOptions: [10, 25, 50, 100],
            },
        },
    ];

    const usersForEditPanel = useMemo(() => {
        if (!editedUser) {
            return rawUsers;
        }

        return rawUsers.some((user) => user.id === editedUser.id)
            ? rawUsers
            : [editedUser, ...rawUsers];
    }, [rawUsers, editedUser]);

    return (
        <div className="users-page">
            <Menu />
            <Content>
                <div className="table-container">
                    <UniversalLineWithComponents leftSide={topLeftSide} rightSide={topRightSide} />

                    {pageTab === 'messageReports' && (
                        <div className="message-reports-grouping-switch" role="tablist" aria-label="Grupowanie zgłoszeń wiadomości">
                            {[
                                ['REPORT', 'Po zgłoszeniach'],
                                ['MESSAGE', 'Po wiadomościach'],
                                ['CHAT', 'Po czatach'],
                            ].map(([grouping, label]) => (
                                <button
                                    type="button"
                                    key={grouping}
                                    className={messageReportsGrouping === grouping ? 'is-active' : ''}
                                    onClick={() => {
                                        setMessageReportsGrouping(grouping);
                                        dispatch(setPage(1));
                                        dispatch(setSorting({ sortBy: 'lastReportedAt', sortDirection: 'DESC' }));
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}

                    <Table
                        headers={headers}
                        rows={rows}
                        columnWidths={currentHeaderWidths}
                        cellClass2="cell-actions"
                        cellClass2ConditionList={[1]}
                        idViewing={false}
                        idArrayId={2}
                    />

                    <div className="table-results-info">
                        <span className="table-results-text">Liczba wyników: {totalCount ?? 0}</span>
                    </div>

                    <UniversalLineWithComponents leftSide={bottomLeftSide} rightSide={bottomRightSide} />
                </div>
            </Content>

            <UserDetailPanel allUsers={rawUsers} sourceTab={pageTab} />
            {editModalOpen && <UserEditPanel allUsers={usersForEditPanel} />}
            <UserMessageModal
                isOpen={messageModalOpen}
                user={messageUser}
                onClose={() => dispatch(closeMessageModal())}
            />
            <Modal isOpen={reportPreviewOpen} onClose={closeReportPreview} className="modal-content-2 reports-preview-modal">
                <h2>Szczegóły zgłoszonego użytkownika</h2>
                {reportPreviewLoading ? (
                    <p>Ładowanie danych...</p>
                ) : (
                    <div className="reports-preview-grid">
                        <div className="reports-preview-profile-card reports-preview-row-full">
                            <ProfileImagePreview
                                profile={reportPreviewUser}
                                userId={reportPreviewMeta?.reportedUserId}
                                name={reportPreviewUser?.nick || reportPreviewMeta?.nickname}
                            />
                            <div>
                                <strong>{reportPreviewUser?.nick || reportPreviewMeta?.nickname || 'Zgłoszony użytkownik'}</strong>
                                <span>Zdjęcie profilowe</span>
                            </div>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Report ID</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.reportId ?? reportPreviewMeta?.id ?? '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">ID zgłoszonego</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.reportedUserId || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">ID zgłaszającego</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.reportSenderId || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Powód</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.reportReason || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Data zgłoszenia</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.reportDate || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">UUID profilu</span>
                            <span className="reports-preview-value">{reportPreviewUser?.uuid || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Email</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.email || reportPreviewUser?.email || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Telefon</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.phone || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Nick</span>
                            <span className="reports-preview-value">{reportPreviewUser?.nick || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Imię i nazwisko</span>
                            <span className="reports-preview-value">{`${reportPreviewUser?.name || ''} ${reportPreviewUser?.surname || ''}`.trim() || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Płeć</span>
                            <span className="reports-preview-value">{reportPreviewMeta?.gender || reportPreviewUser?.optionalSexName || reportPreviewUser?.sex || '-'}</span>
                        </div>
                        <div className="reports-preview-row">
                            <span className="reports-preview-label">Data urodzenia</span>
                            <span className="reports-preview-value">{reportPreviewUser?.birthday || '-'}</span>
                        </div>
                        <div className="reports-preview-row reports-preview-row-full">
                            <span className="reports-preview-label">Opis</span>
                            <span className="reports-preview-value">{reportPreviewUser?.description || '-'}</span>
                        </div>
                    </div>
                )}
            </Modal>
            <MessageReportDetailsModal
                isOpen={messageReportPreviewOpen}
                report={messageReportPreview}
                initialTab={messageReportPreviewTab}
                onClose={closeMessageReportPreview}
                onResolve={resolveMessageReportGroup}
                resolving={resolvingMessageReport}
            />
        </div>
    );
}

export default Profiles;
